import React, { useState } from 'react';
import { translate } from 'react-jhipster';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddCircleIcon from '@mui/icons-material/AddCircle';

// Same rules as the rebate email input on the bank detail page.
const EMAIL_REGEX = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;

const validateEmail = (email: string, existing: string[]) => {
  if (email.length < 6) return translate('global.messages.validate.email.minlength');
  if (email.length > 253) return translate('global.messages.validate.email.maxlength');
  if (!EMAIL_REGEX.test(email)) return translate('global.messages.validate.email.invalid');
  if (existing.includes(email)) return translate('error.emailexists');
  return '';
};

export const isEmailListValid = (emails: string[]) => emails.length > 0;

interface EmailSectionProps {
  value: string[];
  onChange: (value: string[]) => void;
  showErrors: boolean;
}

export const EmailSection = ({ value, onChange, showErrors }: EmailSectionProps) => {
  const [input, setInput] = useState('');
  const [error, setError] = useState('');

  const handleInput = (email: string) => {
    setInput(email);
    setError(email === '' ? '' : validateEmail(email, value));
  };

  const handleAdd = () => {
    const email = input.trim();
    const validation = validateEmail(email, value);
    if (validation) {
      setError(validation);
      return;
    }
    onChange([...value, email]);
    setInput('');
    setError('');
  };

  const listError = showErrors && !isEmailListValid(value);

  return (
    <Box sx={{ maxWidth: { md: '66%' } }}>
      {/* Label above the field: the global Bootstrap `legend` style breaks MUI's floating-label notch. */}
      <Typography component="label" htmlFor="rebate-email-input" variant="body2" sx={{ display: 'block', fontWeight: 500, mb: 0.75 }}>
        {translate('comexApp.bank.rebate.email.label')}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
        <TextField
          id="rebate-email-input"
          fullWidth
          size="small"
          type="email"
          placeholder={translate('comexApp.bank.rebate.email.placeholder')}
          value={input}
          onChange={e => handleInput(e.target.value)}
          onKeyPress={e => {
            if (e.key === 'Enter') handleAdd();
          }}
          error={error !== ''}
          helperText={error || ' '}
        />
        <IconButton color="primary" aria-label="add rebate email" onClick={handleAdd} disabled={input === '' || error !== ''} sx={{ mt: '-2px' }}>
          <AddCircleIcon fontSize="large" />
        </IconButton>
      </Box>

      <Typography variant="body2" sx={{ mt: 1, mb: 1, fontWeight: 500 }}>
        {translate('comexApp.bank.rebate.email.recipients', { count: value.length })}
      </Typography>
      <Box
        sx={{
          minHeight: 56,
          p: 1,
          border: 1,
          borderColor: listError ? 'error.main' : 'divider',
          borderRadius: 1,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1,
        }}
      >
        {value.length > 0 ? (
          value.map(email => <Chip key={email} label={email} onDelete={() => onChange(value.filter(e => e !== email))} />)
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
            {translate('comexApp.bank.rebate.email.empty')}
          </Typography>
        )}
      </Box>
      {listError && (
        <Typography variant="caption" color="error" sx={{ mx: 1.75 }}>
          {translate('comexApp.bank.rebate.email.required')}
        </Typography>
      )}
    </Box>
  );
};
